<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      v-if="isBicycleActiveMode"
      :icon="AddIcon"
      :label="$t('pages.visibility.bicycle.modal.new.buttonText')"
      @click="openVisibilityBicycleModal = true"
    />
    <export
      url="/visibility_bicycles/csv"
      :filter="filter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t(`csv.title.${isBicycleArchivedMode ? 'visibilityBicyclesArchived' : 'visibilityBicycles'}`)"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <visibility-bicycle-modal
    v-model:open="openVisibilityBicycleModal"
    v-model:visibility-bicycle="editedVisibilityBicycle"
    @saved="refetch"
  />
  <div class="main-area h-full">
    <visibility-bicycle-map
      v-if="records?.length && isBicycleActiveMode"
      class="ml-12"
      :busy="loading"
      :points="pointsForMap"
      :checked-ids-list="checkedIdsList"
    />
    <div class="ml-12">
      <div class="text-body-regular-bold text-table-text-gray uppercase mb-5" data-v-67ba7ed1="">
        {{ $t('pages.visibility.bicycle.tableHeader') }}
      </div>
      <div class="flex flex-col">
        <data-table
          v-model:sort-by="sortKey"
          v-model:sort-direction="sortDirection"
          v-model:checked-ids-list="checkedIdsList"
          class="mb-auto"
          :busy="loading"
          :items="records"
          :columns="columns"
          :visible-columns="visibleColumns"
          :actions="actions"
          :choosable="true"
        />
      </div>
    </div>
  </div>
  <pagination
    v-model:page="page"
    v-model:per-page="perPage"
    class="pagination"
    :total="total"
  />
</template>

<script lang="ts" setup>
import {
  computed, reactive, ref, unref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import {
  collOptionsProvider,
  useProvider,
  enumOptionsProvider,
  withExtraOptions,
} from '@utils/table/hooks/provider';
import Action from '@utils/toolbar/Action.vue';
import AddIcon from '@images/panes/add.svg';
import type { Filter } from '@utils/filter/Filter.vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import Export from '@utils/toolbar/panes/Export.vue';
import { useTableFields } from '@hooks/table-configuration';
import { useQueryRef } from '@hooks/url';
import { format } from 'date-fns';
import VisibilityBicycleModal from './VisibilityBicycleModal.vue';
import {
  type HydraCollection,
  type VisibilityBicycle,
  type Region,
  type EnrichedVisibilityBicycleDto,
  VisibilityBicycleViewMode,
  VisibilityBicycleStatus,
} from '@/entities/Entities';
import VisibilityBicycleMap from './VisibilityBicycleMap.vue';

interface Props {
  variant: VisibilityBicycleViewMode;
}
const props = defineProps<Props>();

const isBicycleActiveMode = computed(() => props.variant === VisibilityBicycleViewMode.ACTIVE);
const isBicycleArchivedMode = computed(() => props.variant === VisibilityBicycleViewMode.ARCHIVED);

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 100);

const filter = useQueryRef('filter', {
  ...(!isBicycleActiveMode.value
    ? { status: VisibilityBicycleStatus.DECOMMISSIONED }
    : {
      status: Object.values(VisibilityBicycleStatus).filter(
        (status) => status !== VisibilityBicycleStatus.DECOMMISSIONED,
      ),
    }),
});

const openVisibilityBicycleModal = ref<boolean>(false);
const editedVisibilityBicycle = ref<VisibilityBicycle | null>(null);

const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const { t } = useI18n();

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter,
  pagination: {
    page,
    perPage,
  },
});

const actions = [
  {
    title: t('pages.visibility.bicycle.actions.edit'),
    action: (item: VisibilityBicycle) => {
      openVisibilityBicycleModal.value = true;
      editedVisibilityBicycle.value = { ...item };
    },
  },
];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'visibilityBicycle.id',
    title: t('id'),
    sortable: false,
    group: t('pages.visibility.bicycle.fieldGroups.general'),
    class: 'whitespace-nowrap',
    filter: {
      key: 'id',
      type: 'text',
      group: t('pages.visibility.bicycle.filterGroups.general'),
      options: {
        label: t('id'),
      },
    },
  },
  {
    key: 'visibilityBicycle.region.name',
    title: t('pages.visibility.bicycle.region'),
    group: t('pages.visibility.bicycle.fieldGroups.general'),
    visible: true,
    filter: {
      key: 'region',
      type: 'dropdown',
      group: t('pages.visibility.bicycle.filterGroups.general'),
      options: {
        options: collOptionsProvider<Region>('/regions', {
          sort: {
            sortKey: 'name',
            sortDirection: 'ASC',
          },
        }, (value) => ({
          label: value.name,
          value: value.id,
        })),
        label: t('pages.visibility.bicycle.region'),
      },
    },
  },
  {
    key: 'visibilityBicycle.name',
    title: t('pages.visibility.bicycle.name'),
    group: t('pages.visibility.bicycle.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      key: 'name',
      type: 'text',
      group: t('pages.visibility.bicycle.filterGroups.general'),
      options: {
        label: t('pages.visibility.bicycle.name'),
      },
    },
  },
  ...((props.variant !== VisibilityBicycleViewMode.ARCHIVED ? [
    {
      key: 'visibilityBicycle.status',
      csvTranslations: {
        active: t('pages.visibility.bicycle.visibilityBicycleStatus.active'),
        defective: t('pages.visibility.bicycle.visibilityBicycleStatus.defective'),
        stolen: t('pages.visibility.bicycle.visibilityBicycleStatus.stolen'),
        in_maintenance: t('pages.visibility.bicycle.visibilityBicycleStatus.in_maintenance'),
        lost: t('pages.visibility.bicycle.visibilityBicycleStatus.lost'),
        reserved: t('pages.visibility.bicycle.visibilityBicycleStatus.reserved'),
        in_transit: t('pages.visibility.bicycle.visibilityBicycleStatus.in_transit'),
        decommissioned: t('pages.visibility.bicycle.visibilityBicycleStatus.decommissioned'),
      },
      title: t('pages.visibility.bicycle.status'),
      group: t('pages.visibility.bicycle.fieldGroups.general'),
      sortable: true,
      visible: true,
      formatter: (v: string) => t(`pages.visibility.bicycle.visibilityBicycleStatus.${v}`),
      filter: {
        key: 'status',
        type: 'dropdown',
        group: t('pages.visibility.bicycle.filterGroups.general'),
        options: {
          options: withExtraOptions(
            enumOptionsProvider('/enums/VisibilityBicycleStatus', {}, (value) => ({
              value,
              label: t(`pages.visibility.bicycle.visibilityBicycleStatus.${value}`),
            })),
            [],
            (providerOptions) =>
              providerOptions.filter(
                (option) => option.value !== VisibilityBicycleStatus.DECOMMISSIONED,
              ),
          ),
          label: t('pages.visibility.bicycle.status'),
        },
      },
    },
  ] : []) as ColumnDefinition[]),
  {
    key: 'visibilityBicycle.number',
    title: t('pages.visibility.bicycle.number'),
    group: t('pages.visibility.bicycle.fieldGroups.general'),
    visible: true,
    filter: {
      key: 'number',
      type: 'text',
      group: t('pages.visibility.bicycle.filterGroups.general'),
      options: {
        label: t('pages.visibility.bicycle.number'),
      },
    },
  },
  {
    key: 'visibilityBicycle.currentDesign',
    title: t('pages.visibility.bicycle.currentDesign'),
    group: t('pages.visibility.bicycle.fieldGroups.general'),
    visible: true,
    sortable: false,
    computed: (enrichedVisibilityBicycleDto: EnrichedVisibilityBicycleDto) =>
      enrichedVisibilityBicycleDto.visibilityBicycle.visibilityPlannings?.find(
        (vp) =>
          new Date(vp.startTime as Date) <= new Date() && new Date(vp.endTime as Date) >= new Date(),
      )?.newDesign ?? '-',
  },
  {
    key: 'visibilityBicycle.boxNumber',
    title: t('pages.visibility.bicycle.boxNumber'),
    group: t('pages.visibility.bicycle.fieldGroups.general'),
    visible: true,
    filter: {
      key: 'boxNumber',
      type: 'text',
      group: t('pages.visibility.bicycle.filterGroups.general'),
      options: {
        label: t('pages.visibility.bicycle.boxNumber'),
      },
    },
  },
  {
    key: 'visibilityBicycle.gpsTrackerId',
    title: t('pages.visibility.bicycle.gpsTrackerId'),
    group: t('pages.visibility.bicycle.fieldGroups.general'),
    visible: true,
    filter: {
      key: 'gpsTrackerId',
      type: 'range',
      group: t('pages.visibility.bicycle.filterGroups.general'),
      options: {
        label: t('pages.visibility.bicycle.gpsTrackerId'),
      },
    },
  },
  {
    key: 'visibilityTracker.batteryPct',
    title: t('pages.visibility.bicycle.batteryPct'),
    group: t('pages.visibility.bicycle.fieldGroups.general'),
    visible: true,
    sortable: false,
  },
  {
    key: 'visibilityBicycle.gpsTrackerNumber',
    title: t('pages.visibility.bicycle.gpsTrackerNumber'),
    group: t('pages.visibility.bicycle.fieldGroups.general'),
    visible: true,
    filter: {
      key: 'gpsTrackerNumber',
      type: 'range',
      group: t('pages.visibility.bicycle.filterGroups.general'),
      options: {
        label: t('pages.visibility.bicycle.gpsTrackerNumber'),
      },
    },
  },
  {
    key: 'visibilityBicycle.brand',
    title: t('pages.visibility.bicycle.brand'),
    group: t('pages.visibility.bicycle.fieldGroups.general'),
    visible: true,
    filter: {
      key: 'brand',
      type: 'text',
      group: t('pages.visibility.bicycle.filterGroups.general'),
      options: {
        label: t('pages.visibility.bicycle.brand'),
      },
    },
  },
  {
    key: 'visibilityBicycle.color',
    title: t('pages.visibility.bicycle.color'),
    group: t('pages.visibility.bicycle.fieldGroups.general'),
    visible: true,
    filter: {
      key: 'color',
      type: 'text',
      group: t('pages.visibility.bicycle.filterGroups.general'),
      options: {
        label: t('pages.visibility.bicycle.color'),
      },
    },
  },
  {
    key: 'visibilityBicycle.comment',
    title: t('pages.visibility.bicycle.comment'),
    group: t('pages.visibility.bicycle.fieldGroups.general'),
    visible: true,
    filter: {
      key: 'comment',
      type: 'text',
      group: t('pages.visibility.bicycle.filterGroups.general'),
      options: {
        label: t('pages.visibility.bicycle.comment'),
      },
    },
  },
  {
    key: 'visibilityBicycle.createdAt',
    title: t('createdAt'),
    group: t('pages.visibility.bicycle.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : ''),
    filter: {
      key: 'createdAt',
      type: 'datetime',
      group: t('pages.visibility.bicycle.filterGroups.general'),
      options: {
        label: t('createdAt'),
      },
    },
  },
  {
    key: 'visibilityBicycle.updatedAt',
    title: t('updatedAt'),
    group: t('pages.visibility.bicycle.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : ''),
    filter: {
      key: 'updatedAt',
      type: 'datetime',
      group: t('pages.visibility.bicycle.filterGroups.general'),
      options: {
        label: t('updatedAt'),
      },
    },
  },
];

const visibleColumns = useTableFields(
  columns,
);

const {
  data: items,
  loading: loadingTable,
  refetch,
} = useProvider<HydraCollection<EnrichedVisibilityBicycleDto>>('/visibility_bicycles/tracker', providerParams);

const records = computed(() => unref(items)?.['hydra:member']?.map((i) => ({ ...i, id: i.visibilityBicycle.id })));
const total = computed(() => unref(items)?.['hydra:totalItems']);

const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter)) {
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    }
    return [{ key: col.key, ...col.filter }];
  }));

const loading = loadingTable;

const checkedIdsList = ref<string[]>([]);
const pointsForMap = computed(() => checkedIdsList.value.map((id) => records.value?.find((record) => record?.id === id)));
</script>
